import { InitialOptionValue, LimitEntry } from "@types";
import { DefectOptionItem } from "@types";

// augmentation이 아니어도 옵션 설정에 같이 보여줄 모듈 키 (mode가 enabled/disabled이고 백엔드 단계 변환이 건드리지 않는 것만)
const EXTRA_OPTION_KEYS = ["prepro_crop"];

// 파이프라인 연결: 모든 모듈의 connection.in은 자기 자신("키=0"), out은 다음 모듈.
// augmentation 체인은 input → ignore_rot → prepro_crop → … → (마지막 옵션) → expanddim
const CHAIN_START = "ignore_rot";
const CHAIN_END = "expanddim";
const nextKeyOf = (option?: DefectOptionItem): string | undefined =>
  option?.para?.connection?.out?.[0]?.[0]?.split("=")[0];

// 체인 입구: input 모듈의 첫 out 대상 중 실제로 있는 모듈 (보통 ignore_rot). input을 못 찾으면 CHAIN_START
const inputKeyOf = (options: Record<string, DefectOptionItem>) =>
  Object.keys(options).find((key) => options[key]?.tag?.includes("input"));
function chainHead(options: Record<string, DefectOptionItem>): string {
  const input = inputKeyOf(options);
  const targets: string[] = input ? (options[input].para?.connection?.out?.[0] ?? []) : [];
  return targets.map((t) => t.split("=")[0]).find((key) => key in options) ?? CHAIN_START;
}

// 체인 입구부터 out을 따라간 순서 (CHAIN_END나 모르는 키에서 멈춤)
function chainOrder(options: Record<string, DefectOptionItem>): string[] {
  const order: string[] = [];
  let key: string | undefined = chainHead(options);
  while (key && key !== CHAIN_END && key in options && !order.includes(key)) {
    order.push(key);
    key = nextKeyOf(options[key]);
  }
  return order;
}

// 옵션 설정에 보여줄 옵션을 파이프라인 순서로 (ignore_rot가 첫 번째, expanddim 직전 옵션이 마지막)
function extractAugmentationOptions(moduleOptions: Record<string, DefectOptionItem>): Record<string, DefectOptionItem> {
  const shown = (key: string) => moduleOptions[key].tag.includes("augmentation") || EXTRA_OPTION_KEYS.includes(key);
  const chained = chainOrder(moduleOptions);
  const keys = [...chained, ...Object.keys(moduleOptions).filter((key) => !chained.includes(key))].filter(shown);
  return Object.fromEntries(keys.map((key) => [key, moduleOptions[key]]));
}

// 체인 위치 권장 규칙 (강제하지 않고 경고만): ignore_rot 맨 앞, 맨 뒤는 ignore_brightNcont → HistogramEqualizationGray 옵션들
const TAIL_KEY = "ignore_brightNcont";
const LAST_MODULE_NAME = "HistogramEqualizationGray";
const isTailOption = (key: string, option?: { name?: string }) => key === TAIL_KEY || option?.name === LAST_MODULE_NAME;

function chainOrderWarnings(options: Record<string, DefectOptionItem>): string[] {
  const keys = Object.keys(options);
  const warnings: string[] = [];
  if (keys.includes(CHAIN_START) && keys[0] !== CHAIN_START)
    warnings.push(`${CHAIN_START}는 첫 번째 옵션으로 두는 것을 권장합니다.`);
  const tail = [
    ...keys.filter((key) => key === TAIL_KEY),
    ...keys.filter((key) => options[key]?.name === LAST_MODULE_NAME),
  ];
  if (tail.length && keys.slice(-tail.length).join() !== tail.join())
    warnings.push(`마지막은 ${tail.join(" → ")} 순서로 두는 것을 권장합니다.`);
  return warnings;
}

// 새/다시 추가하는 옵션을 넣을 위치: ignore_rot는 맨 앞, 끝 옵션(ignore_brightNcont, histogram)은 규칙 자리, 그 외는 끝 옵션들 앞
function insertOption(options: Record<string, any>, key: string, option: any): Record<string, any> {
  const entries = Object.entries<any>(options).filter(([k]) => k !== key);
  const histAt = entries.findIndex(([, o]) => o?.name === LAST_MODULE_NAME);
  let at = entries.findIndex(([k, o]) => isTailOption(k, o));
  if (key === CHAIN_START) at = 0;
  else if (option?.name === LAST_MODULE_NAME) at = -1;
  else if (key === TAIL_KEY) at = histAt;
  if (at < 0) at = entries.length;
  return Object.fromEntries([...entries.slice(0, at), [key, option], ...entries.slice(at)]);
}

// connection은 순서에서 자동 계산되므로 JSON에서는 보여주지도, 받지도 않는다
function withoutConnection(para: Record<string, any> = {}) {
  const rest = { ...para };
  delete rest.connection;
  return rest;
}

// connection = edited 키 순서(SIMPLE 목록/JSON 줄 순서). 각 모듈 in = 자기 자신, out = 다음 모듈, 마지막은 expanddim
function wireChain(
  edited: Record<string, DefectOptionItem>,
  current: Record<string, DefectOptionItem>, // AI 서버의 현재 모듈 옵션 전체
): Record<string, DefectOptionItem> {
  const all = { ...current, ...edited };
  const existing = chainOrder(current);
  if (!existing.length || nextKeyOf(current[existing[existing.length - 1]]) !== CHAIN_END)
    throw new Error(`input에서 ${CHAIN_END}까지 이어지는 체인을 찾지 못해 옵션을 연결할 수 없습니다.`);

  // 화면에 없는 체인 모듈은 바로 앞 옵션에 묶는다 (예: prepro_crop → [prepro_crop, prepro_normalize])
  const followers: Record<string, string[]> = {};
  let anchor = "";
  for (const key of existing) {
    if (key in edited) anchor = key;
    else (followers[anchor] ??= []).push(key);
  }
  // 체인에 연결돼 있거나 새로 추가한 옵션만 체인에 넣는다 (체인 밖 옵션은 건드리지 않음)
  const shown = Object.keys(edited).filter((key) => existing.includes(key) || !(key in current));
  const order = [...(followers[""] ?? []), ...shown.flatMap((key) => [key, ...(followers[key] ?? [])])];

  const wired: Record<string, DefectOptionItem> = { ...edited };
  order.forEach((key, i) => {
    const connection = { in: [[`${key}=0`]], out: [[`${order[i + 1] ?? CHAIN_END}=0`]] };
    if (JSON.stringify(all[key].para?.connection) !== JSON.stringify(connection))
      wired[key] = { ...all[key], para: { ...all[key].para, connection } };
  });

  // 맨 앞 옵션이 바뀌면 input → 체인 입구 연결도 새 첫 옵션으로 (그대로 두면 앞으로 옮긴 옵션들이 끊긴다)
  const head = existing[0];
  const input = inputKeyOf(current);
  if (input && order[0] !== head) {
    const conn = current[input].para.connection;
    const out = conn.out.map((group: string[], g: number) =>
      g === 0 ? group.map((t) => (t === `${head}=0` ? `${order[0]}=0` : t)) : group,
    );
    wired[input] = { ...current[input], para: { ...current[input].para, connection: { ...conn, out } } };
  }
  return wired;
}

function resolveParamLimits(
  item: DefectOptionItem,
  initialOptions: Record<string, InitialOptionValue>,
): Record<string, LimitEntry> {
  const limit = initialOptions[item.name]?.limit ?? {};

  return Object.fromEntries(
    Object.keys(item.para)
      .filter((key) => key in limit)
      .map((key) => [key, limit[key]]),
  );
}

function optionsToJson(options: Record<string, DefectOptionItem>, pretty = false): string {
  const shown = Object.entries(options)
    .filter(([, { para }]) => para?.mode !== "disabled")
    .map(([key, { name, para }]) => [key, { name, para: withoutConnection(para) }] as const);
  if (pretty) return JSON.stringify(Object.fromEntries(shown), null, 2);
  const lines = shown.map(([key, item]) => `  ${JSON.stringify(key)}: ${JSON.stringify(item)}`);
  return `{\n${lines.join(",\n")}\n}`;
}

function parseOptionsJson(
  text: string,
  base: Record<string, DefectOptionItem>,
  reserved: string[] = [],
): Record<string, DefectOptionItem> {
  const parsed = JSON.parse(text);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    throw new Error("최상위는 { 옵션키: {...} } 객체여야 합니다.");

  const seen = new Set<string>();
  for (const key of topLevelKeys(text)) {
    if (seen.has(key)) throw new Error(`${key}: 옵션 키가 중복됩니다.`);
    seen.add(key);
  }

  const next: Record<string, DefectOptionItem> = {};
  for (const [key, item] of Object.entries<any>(parsed)) {
    if (typeof item?.name !== "string" || !item.para || typeof item.para !== "object")
      throw new Error(`${key}: name(문자열)과 para(객체)가 필요합니다.`);
    if (reserved.includes(key)) throw new Error(`${key}: augmentation이 아닌 모듈 키라 쓸 수 없습니다.`);
    if (base[key] && base[key].name !== item.name)
      throw new Error(`${key}: 이미 있는 옵션 키입니다(disabled 포함). 새 옵션은 다른 키를 쓰세요.`);
    // 기존 옵션은 원래 name 그대로 허용, 새 옵션은 정해진 augmentation 모듈 이름만
    if (!base[key] && !AUG_MODULE_NAMES.includes(item.name))
      throw new Error(`${key}: name은 다음 중 하나여야 합니다: ${AUG_MODULE_NAMES.join(", ")}`);
    // 새 옵션은 AI 서버 모듈 형식대로 desc {} / tag 기본값, 기존 옵션은 서버 값 유지
    // connection은 사용자 입력을 버리고 기존 값 유지 (Save 때 줄 순서로 wireChain이 다시 계산)
    const connection = base[key]?.para?.connection;
    next[key] = {
      name: item.name,
      desc: base[key]?.desc ?? {},
      para: { ...withoutConnection(item.para), ...(connection && { connection }) },
      tag: base[key]?.tag ?? NEW_OPTION_TAG,
    };
  }
  // JSON에 없는 기존 옵션(disabled라 숨김/지움)은 disabled로 남기되, 원래 순서에서 바로 앞 옵션 뒤에 둔다
  // → 키 순서 = 체인 순서(wireChain)라서, 맨 뒤로 보내면 저장할 때마다 숨긴 옵션의 체인 위치가 바뀐다
  const hiddenAfter: Record<string, string[]> = {};
  let anchor = "";
  for (const [key, item] of Object.entries(base)) {
    if (key in next) anchor = key;
    else if (item.para?.mode !== undefined) (hiddenAfter[anchor] ??= []).push(key);
  }
  const ordered: Record<string, DefectOptionItem> = {};
  const putHidden = (after: string) =>
    (hiddenAfter[after] ?? []).forEach((key) => {
      ordered[key] = { ...base[key], para: { ...base[key].para, mode: "disabled" } };
    });
  putHidden("");
  for (const [key, item] of Object.entries(next)) {
    ordered[key] = item;
    putHidden(key);
  }
  return ordered;
}

// 새로 추가할 수 있는 augmentation 모듈 이름
const AUG_MODULE_NAMES = [
  "AugmentBlur",
  "CropSquare",
  "AugmentRotRightNFlip",
  "HistogramEqualizationGray",
  "AugmentRotationNBrightness",
  "AugmentColor",
  "AugmentShrinkEdgeGray",
  "AugmentObjectPolygonGray",
  "AugmentObjectCircleGray",
  "AugmentObjectLineGray",
  "AugmentObjectPolylinesGray",
  "AugmentObjectParallelGray",
  "AugmentDistortBGR",
  "AugmentObjectPolygonBGR",
  "AugmentObjectCircleBGR",
  "AugmentDistortMask",
];
const NEW_OPTION_TAG = ["augmentation", "multiprocess", "train_on"];

function addNewOptionToJson(
  text: string,
  name: string,
  initPara: Record<string, any> | undefined, // AI 초기설정의 init. 없으면(초기설정에 없는 모듈) para는 {}로 두고 사용자가 채운다
  taken: string[],
  pretty: boolean,
) {
  const current = JSON.parse(text);
  const used = new Set([...Object.keys(current), ...taken]);
  const prefix = name.replace(/^Augment/, "").toLowerCase();
  let n = 1;
  while (used.has(`${prefix}_${n}`)) n++;
  const para = initPara ? { ...initPara, mode: "enabled" } : {};
  return optionsToJson(insertOption(current, `${prefix}_${n}`, { name, para, tag: NEW_OPTION_TAG }), pretty);
}

// 최상위 객체의 키를 등장 순서대로
function topLevelKeys(text: string): string[] {
  const keys: string[] = [];
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      let j = i + 1;
      while (text[j] !== '"') j += text[j] === "\\" ? 2 : 1;
      if (depth === 1 && /^\s*:/.test(text.slice(j + 1))) keys.push(JSON.parse(text.slice(i, j + 1)));
      i = j;
    } else if (c === "{" || c === "[") depth++;
    else if (c === "}" || c === "]") depth--;
  }
  return keys;
}

// JSON에 없는(지웠거나 disabled라 숨겨진) 기존 옵션 키 → JSON에서 다시 추가할 후보
function hiddenOptionKeys(text: string, base: Record<string, DefectOptionItem>): string[] {
  const shown = new Set(topLevelKeys(text));
  return Object.keys(base).filter((key) => !shown.has(key));
}

// 숨겨진 옵션을 원래 name/para 그대로, enabled로 JSON에 다시 넣는다
function restoreOptionToJson(text: string, base: Record<string, DefectOptionItem>, key: string, pretty: boolean) {
  const { name, para } = base[key];
  const restored = { name, para: para?.mode === undefined ? para : { ...para, mode: "enabled" }, tag: base[key].tag };
  return optionsToJson(insertOption(JSON.parse(text), key, restored), pretty);
}

export {
  wireChain,
  chainOrderWarnings,
  withoutConnection,
  AUG_MODULE_NAMES,
  addNewOptionToJson,
  topLevelKeys,
  hiddenOptionKeys,
  restoreOptionToJson,
  extractAugmentationOptions,
  resolveParamLimits,
  optionsToJson,
  parseOptionsJson,
};
